import * as React from 'react';
import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { mergeRight } from 'ramda';
import { pageSetting } from './data/page-setting';
import * as icon from './data/icon';
import PageList from './page-list';
import IconList from './icon-list';
import IconListEditor from './icon-list-editor';
import Scope, { controller as ScopeController, noop } from './scope';
import { PageNumber } from './page-number';
import Popup from './popup';
import PopupMini from './popup-mini';
import DrawingBoard, { controller as DrawingBoardCtl } from './drawing-board';
import Toolbar from './toolbar';
import { assert } from '../lib/utils';

import setting from '../../setting';

const css = require('./index.css').default;

function Root() {
    const [pageSetting, setPageSetting] = useState<{
        pageSize: size,
        pages: pageSetting[],
    } | null>(null);

    useEffect(() => {
        (async () => {
            const res = await fetch(`${setting.bookDir}/pages/index.json`);
            setPageSetting(await res.json());
        })();
    }, []);

    if (pageSetting === null) {
        return <h1>載入中</h1>;
    } else {
        return <App pages={pageSetting.pages} pageSize={pageSetting.pageSize}/>;
    }
}

function App({ pages, pageSize }: {
    pages: pageSetting[],
    pageSize: size,
}) {
    const [screenSize, setScreenSize] = useState<size>({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [activePage, setActivePage] = useState(0);
    const [scopeCtl, setScopeCtl] = useState<ScopeController>(noop);
    const [popup, setPopup] = useState<string | null>(null);
    const [popupMini, setPopupMini] = useState<{
        data: string,
        viewport: viewport,
    } | null>(null);
    const [drawingType, setDrawingType] = useState<'pen' | 'eraser' | null>(null);
    const [drawingCtl, setDrawingCtl] = useState<DrawingBoardCtl | null>(null);
    const [accent, setAccent] = useState(localStorage.getItem('腔調') || '通行腔');

    useEffect(() => {
        window.addEventListener('resize', () => setScreenSize({
            width: window.innerWidth,
            height: window.innerHeight,
        }));
    }, []);

    useEffect(() => {
        window.addEventListener('popup', e => {
            // 從 iframe window 送出的 CustomEvent 跟這個 window 的 CustomEvent 不一樣
            // 所以不能 assert(e instanceof CustomEvent, '')
            const url = (e as any)['detail'];
            assert(typeof url === 'string', '');
            setPopup(`${setting.bookDir}/補充資料/${url}`);
        });
    }, []);

    useEffect(() => {
        if (localStorage.getItem('腔調') === null) {
            localStorage.setItem('腔調', accent);
        } else {
            localStorage.setItem('腔調', accent);
            window.dispatchEvent(new CustomEvent('accent', { detail: accent }));
            document.querySelectorAll('iframe')
            .forEach(iframe => {
                iframe.contentWindow?.dispatchEvent(new CustomEvent('accent', { detail: accent }));
            });
        }
    }, [accent]);

    const bookViewSize = {
        width: screenSize.width - 80,
        height: screenSize.height,
    }

    const editMode = new URL(document.location.href).searchParams.get('mode') === 'edit';

    return (
    <>
        <div id={css.scopeLayer}>
            <Scope oriSize={pageSize} viewSize={bookViewSize} onInit={setScopeCtl}>
                <PageList
                    pages={pages.map(page => page.url)} active={activePage}
                    pageSize={pageSize} viewSize={bookViewSize}
                    scopeCtl={scopeCtl}/>
                {(() => {
                    if (editMode) {
                        return (
                        <IconListEditor
                            page={pages[activePage].url}
                        />);
                    } else {
                        return (
                        <IconList
                            page={pages[activePage].url}
                            style={{
                                position: 'absolute',
                                width: `${pageSize.width}px`,
                                height: `${pageSize.height}px`,
                                pointerEvents: 'none',
                            }}
                            accent={accent}
                            onSelect={(e, command) => {
                                if (icon.command.isText(command) || icon.command.isImg(command)) {
                                    setPopupMini({
                                        data: command,
                                        viewport: (e.target as HTMLElement).getBoundingClientRect(),
                                    });
                                } else if (icon.command.isGoto(command)) {
                                    const idx = findActivePage(pages, icon.command.stripPrefix(command));
                                    if (idx !== -1) {
                                        setActivePage(idx);
                                    }
                                } else {
                                    window.dispatchEvent(new CustomEvent('popup', { detail: command }));
                                }
                            }}
                        />);
                    }
                })()}
                <PageNumber
                    style={{
                        position: 'absolute',
                        width: `${pageSize.width}px`,
                        height: `${pageSize.height}px`,
                    }}
                    pns={pages[activePage].page}
                />
                <DrawingBoard
                    type={drawingType}
                    width={pageSize.width}
                    height={pageSize.height}
                    style={{ position: 'absolute' }}
                    onInit={setDrawingCtl}
                />
            </Scope>
        </div>

        {(() => {
            if (popup !== null) {
                return (
                <>
                    <Popup src={popup}
                        screenViewport={mergeRight(screenSize, { x: 0, y: 0 })}
                        onClose={() => setPopup(null)}
                    />
                    <DrawingBoard
                        type={drawingType}
                        width={screenSize.width}
                        height={screenSize.height}
                        style={{ position: 'absolute', top: '0px', left: '0px' }}
                    />
                </>);
            }
        })()}

        {(() => {
            if (popupMini !== null) {
                return <PopupMini data={popupMini.data} source={popupMini.viewport} onClose={() => setPopupMini(null)}/>;
            }
        })()}

        <Toolbar
            style={{
                position: 'absolute',
                top: '0px',
                right: '3px',
                width: '80px',
                textAlign: 'right',
            }}
            scopeCtl={scopeCtl}
            accent={accent} setAccent={setAccent}
            drawingType={drawingType} setDrawingType={setDrawingType}
            onPrevPage={() => {
                if (activePage > 0) {
                    scopeCtl.fit();
                    setActivePage(activePage - 1);
                }
            }}
            onNextPage={() => {
                if (activePage < pages.length - 1) {
                    scopeCtl.fit();
                    setActivePage(activePage + 1);
                }
            }}
            onPage={page => {
                const idx = findActivePage(pages, page.toString());
                if (idx !== -1) {
                    setActivePage(idx);
                }
            }}
            onSave={() => {
                assert(drawingCtl !== null, '');
                localStorage.setItem('canvas', drawingCtl.export());
            }}
            onLoad={() => {
                const data = localStorage.getItem('canvas');
                if (data !== null) {
                    drawingCtl?.import(data);
                }
            }}
        />
    </>);
}

const root = createRoot(document.getElementById('root')!);
root.render(<Root/>);

function findActivePage(pages: pageSetting[], 頁碼: string) {
    return pages.findIndex(setting => {
        return setting.page.map(p => p.toString()).includes(頁碼.trim());
    });
}

type size = {
    width: number,
    height: number,
}

type offset = {
    x: number,
    y: number,
}

type position = offset;
type viewport = size & position;
